import React, { useEffect, useRef, useState } from 'react';
import cls from 'classnames';
import { isEnterKey, isEscapeKey } from '../../../utils';
import { Button } from '../Button/Button';
import { TooltipTrigger } from '../Tooltip/TooltipTrigger';
import styles from "./EditableHeader.module.scss";
import { text } from '../../../utils/i18n';

type SaveResult = { value: string; };

export type EditableHeaderRef = {
  isOpen: () => boolean;
  open: () => void;
  cancel: () => void;
  save: () => Promise<SaveResult>;
}

type Props = {
  className?: string;
  onSave: (value: string) => void | boolean | Promise<void> | Promise<boolean>;
  onChange?: (value: string) => void;
  showTaskIndicator?: boolean;
  children?: any;
  value: string;
  placeholder?: React.ReactNode;

  /**
   * Optional list of placeholders to cycle through, such as value examples.
   */
  placeholders?: React.ReactNode[];
  defaultEditable?: boolean;
  editable?: boolean;
  readOnly?: boolean;
  onEditableChange?: (editable: boolean) => void;
  editTooltipContent?: React.ReactNode;

  /**
   * Allows remote components to control this editable header.
   */
  controller?: (ref: EditableHeaderRef) => void;

  /**
   * Whether to wrap text into rows.
   * @default true
   */
  multiline?: boolean | 'whenEditable';
}

/**
 * Renders a `<h1>` which can be editable.
 * 
 * Note: Use this component in place of a text input **to prevent accidental changes**; for instance, in a view-level ribbon.
 * Do not use this in a modal. Standard settings modals require that the user click on a settings item and then click
 * "save" for the changes to be applied, so it's difficult to make accidental changes. In that case, use a TextInput instead.
 */
export const EditableHeader: React.FC<Props> = ({
  className,
  onSave,
  value,
  showTaskIndicator,
  children,
  placeholder,
  placeholders,
  editable: explicitEditable,
  defaultEditable = false,
  readOnly,
  onEditableChange,
  editTooltipContent = text`Redigera`,
  controller,
  multiline = true
}) => {
  const inputRef = React.useRef<HTMLElement>(null);
  const shouldAutoFocus = React.useRef(true);

  const [cycledPlaceholderIndex, setCycledPlaceholderIndex] = useState<number>(null);
  const [cycledPlaceholderActive, setCycledPlaceholderActive] = useState<boolean>(placeholders?.length > 0); // Used for appear / disappear animation.
  const [isFocus, setFocus] = useState(false);
  let [editable, _setEditable] = React.useState(defaultEditable);
  const editableRef = useRef(editable);
  editableRef.current = editable;

  if (multiline === "whenEditable") {
    multiline = editable;
  }

  function setEditable(flag: boolean) {
    if (flag) {
      shouldAutoFocus.current = true;
    }

    _setEditable(flag);
    onEditableChange?.(flag);
  }

  if (typeof explicitEditable == 'boolean') {
    editable = explicitEditable;
  }

  async function save(): Promise<SaveResult> {
    const value = inputRef.current.innerText.trim();
    if (!editableRef.current) {
      //  save() may be called any time to get the current value.
      return { value };
    }

    if (await onSave(value) === true) {
      setEditable(false);
      setFocus(false);
    }

    return { value };
  }

  function cancel() {
    setEditable(false);
    setFocus(false);
    inputRef.current.innerText = value;
  }

  useEffect(() => {
    controller?.({
      isOpen: () => editableRef.current,
      cancel,
      save,
      open: () => setEditable(true)
    });
  }, []);

  const cycledPlaceholder = placeholders?.length ? placeholders[cycledPlaceholderIndex % placeholders.length] : null;
  if (!placeholder) {
    placeholder = cycledPlaceholder;
  }

  useEffect(() => {
    //  When placeholder index changes, inactive the current placeholder after a while.
    const inactiveTimeout = setTimeout(() => setCycledPlaceholderActive(false), 3500);
    return () => { clearTimeout(inactiveTimeout); }
  }, [cycledPlaceholderIndex]);

  useEffect(() => {
    if (!placeholders?.length) {
      return;
    }

    if (!cycledPlaceholderActive) {
      const cycleTimeout = setTimeout(() => {
        setCycledPlaceholderIndex(index => index + 1);
        setCycledPlaceholderActive(true);
      }, 1000);
      return () => { clearTimeout(cycleTimeout); }
    }

  }, [cycledPlaceholderActive, placeholders?.length]);


  useEffect(() => {
    if (placeholders?.length) {
      //  Start/reset cycle.
      setCycledPlaceholderActive(true);
      setCycledPlaceholderIndex(0);
    }
  }, [placeholders?.length]);

  return (
    <h1
      spellCheck={false}
      className={cls([styles.header, multiline ? styles.multiline : null, className])}
      onKeyDown={e => {
        if (isEnterKey(e)) {
          save();
          e.preventDefault();
          e.stopPropagation();
        } else if (isEscapeKey(e)) {
          cancel();

          e.preventDefault();
          e.stopPropagation();
        }
      }}>
      <div
        className={cls([styles.input, editable ? styles.editable : null, isFocus && editable ? styles.focus : null])}>
        <div className={styles.scrollableValueContainer}>
          <span
            ref={e => {
              inputRef.current = e;
              if (e && shouldAutoFocus.current) {
                e.focus();
                shouldAutoFocus.current = false;
              }
            }}
            className={styles.value}
            contentEditable={editable}
            role="button"
            onClick={(e) => {
              if (!editable) {
                setEditable(true);
              }
            }}
            onFocus={() => setFocus(true)}
            onBlur={() => setFocus(false)}>
            {value}
          </span>
          {
            placeholder ? (
              <span
                className={cls([styles.placeholder, cycledPlaceholder ? styles.cycled : null, cycledPlaceholderActive ? styles.cycledActive : null])}>
                {placeholder}
              </span>
            ) : null
          }
        </div>
      </div>

      {
        editable ? (
          <>
            <Button
              onClick={() => save()}
              showTaskIndicator={showTaskIndicator}
              icon="check" />

            <Button
              onClick={() => cancel()}
              disabled={showTaskIndicator}
              icon="times" />
          </>
        ) : !readOnly ? (
          <TooltipTrigger tooltip={editTooltipContent}>
            <Button
              onClick={() => setEditable(true)}
              icon="pen" />
          </TooltipTrigger>
        ) : null
      }
      {children}
    </h1>
  );
}